import { Link } from '@/i18n/routing'
import type { MouseEvent } from 'react'
import type { SearchResult } from '@/types/dictionary'
import type { CardInfo, BookmarkInfo } from '@/types/collections'
import { cn } from '@/lib/utils'
import { LabelText, MutedText } from '@/components/ui/typography'
import { useSettings } from '@/hooks/useSettings'
import { useChineseFont } from '@/lib/chinese-font'
import { CardInfoBadge } from '@/components/lists/card-info-badge'
import { BookmarkInfoBadge } from '@/components/lists/bookmark-info-badge'
import { Button } from '@/components/ui/button'
import { IconX } from '@tabler/icons-react'

interface Props {
  item: SearchResult
  onClick?: (item: SearchResult) => void
  href?: string
  cardInfo?: CardInfo
  bookmarkInfo?: BookmarkInfo
  onRemove?: (item: SearchResult) => void
  isRemoving?: boolean
}

export function SearchResultItem({
  item,
  onClick,
  href,
  cardInfo,
  bookmarkInfo,
  onRemove,
  isRemoving = false
}: Props) {
  const { settings } = useSettings()
  const chineseFont = useChineseFont()
  const { simplified, traditional, pinyin, zhuyin } = item

  // Script display logic
  const hasTraditional = traditional && traditional !== simplified

  const getPrimaryScript = () => {
    if (settings.scriptDisplay === 'traditional' && hasTraditional) {
      return traditional
    }
    if (settings.scriptDisplay === 'simplified') {
      return simplified
    }
    // For 'both', use priority
    if (settings.scriptDisplay === 'both') {
      return settings.scriptPriority === 'traditional' && hasTraditional
        ? traditional
        : simplified
    }
    return simplified
  }

  const getSecondaryScript = () => {
    if (settings.scriptDisplay !== 'both') return null
    const primary = getPrimaryScript()
    if (primary === simplified && hasTraditional) {
      return traditional
    }
    if (primary === traditional) {
      return simplified
    }
    return null
  }

  const primaryScript = getPrimaryScript()
  const secondaryScript = getSecondaryScript()

  // Pronunciation display logic
  const getPrimaryPronunciation = () => {
    if (settings.pronunciationDisplay === 'pinyin') {
      return pinyin
    }
    if (settings.pronunciationDisplay === 'zhuyin' && zhuyin) {
      return zhuyin
    }
    // Both mode - use priority
    if (settings.pronunciationDisplay === 'both') {
      return settings.pronunciationPriority === 'pinyin'
        ? pinyin
        : zhuyin || pinyin
    }
    return pinyin
  }

  const getSecondaryPronunciation = () => {
    if (settings.pronunciationDisplay !== 'both') return null
    const primary = getPrimaryPronunciation()
    if (primary === pinyin) {
      return zhuyin || null
    }
    if (primary === zhuyin) {
      return pinyin
    }
    return null
  }

  const primaryPronunciation = getPrimaryPronunciation()
  const secondaryPronunciation = getSecondaryPronunciation()

  const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onRemove?.(item)
  }

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Keep modifier/middle-click behavior for opening in a new tab/window.
    const isModifiedClick =
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey

    if (!onClick || isModifiedClick) {
      return
    }

    // Use the callback as the source of truth for in-page selection state.
    event.preventDefault()
    onClick(item)
  }

  return (
    <div className="group relative">
      {href ? (
        <Link
          href={href}
          onClick={handleLinkClick}
          className={cn(
            'hover:bg-muted/60 focus-visible:ring-ring block w-full rounded-md p-3 pr-11 text-left transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:rounded-lg sm:p-4 sm:pr-12',
            'cursor-pointer select-text'
          )}
        >
          <div className="flex flex-col gap-1.5 sm:gap-2">
            {/* Script (Hanzi) */}
            <div className="flex min-w-0 items-baseline gap-3">
              <span
                className={cn(
                  chineseFont,
                  'text-foreground truncate text-xl tracking-tight sm:text-2xl'
                )}
                title={primaryScript}
              >
                {primaryScript}
              </span>
              {secondaryScript && (
                <span
                  className={cn(
                    chineseFont,
                    'text-muted-foreground/70 truncate text-sm sm:text-base'
                  )}
                  title={secondaryScript}
                >
                  {secondaryScript}
                </span>
              )}
            </div>

            {/* Pronunciation */}
            <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:gap-2">
              <LabelText
                className={cn(
                  // Pinyin uses UI font (Nunito), Zhuyin uses system fonts
                  primaryPronunciation === zhuyin ? 'font-sans' : 'font-ui',
                  'text-muted-foreground/70 truncate'
                )}
              >
                {primaryPronunciation}
              </LabelText>
              {secondaryPronunciation && (
                <LabelText
                  className={cn(
                    // Pinyin uses UI font (Nunito), Zhuyin uses system fonts
                    secondaryPronunciation === zhuyin ? 'font-sans' : 'font-ui',
                    'text-muted-foreground/70 truncate'
                  )}
                >
                  {secondaryPronunciation}
                </LabelText>
              )}
            </div>

            {/* Preview text */}
            <MutedText className="line-clamp-2 text-xs leading-relaxed sm:text-sm">
              {item.preview}
            </MutedText>

            {/* Info badges (bookmark date and/or card info) */}
            {(bookmarkInfo || cardInfo) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {bookmarkInfo && (
                  <BookmarkInfoBadge bookmarkInfo={bookmarkInfo} />
                )}
                {cardInfo && <CardInfoBadge cardInfo={cardInfo} />}
              </div>
            )}
          </div>
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => onClick?.(item)}
          className={cn(
            'hover:bg-muted/60 focus-visible:ring-ring block w-full rounded-md p-3 pr-11 text-left transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:rounded-lg sm:p-4 sm:pr-12',
            'cursor-pointer select-text'
          )}
        >
          <div className="flex flex-col gap-1.5 sm:gap-2">
            {/* Script (Hanzi) */}
            <div className="flex min-w-0 items-baseline gap-3">
              <span
                className={cn(
                  chineseFont,
                  'text-foreground truncate text-xl tracking-tight sm:text-2xl'
                )}
                title={primaryScript}
              >
                {primaryScript}
              </span>
              {secondaryScript && (
                <span
                  className={cn(
                    chineseFont,
                    'text-muted-foreground/70 truncate text-sm sm:text-base'
                  )}
                  title={secondaryScript}
                >
                  {secondaryScript}
                </span>
              )}
            </div>

            {/* Pronunciation */}
            <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:gap-2">
              <LabelText
                className={cn(
                  // Pinyin uses UI font (Nunito), Zhuyin uses system fonts
                  primaryPronunciation === zhuyin ? 'font-sans' : 'font-ui',
                  'text-muted-foreground/70 truncate'
                )}
              >
                {primaryPronunciation}
              </LabelText>
              {secondaryPronunciation && (
                <LabelText
                  className={cn(
                    // Pinyin uses UI font (Nunito), Zhuyin uses system fonts
                    secondaryPronunciation === zhuyin ? 'font-sans' : 'font-ui',
                    'text-muted-foreground/70 truncate'
                  )}
                >
                  {secondaryPronunciation}
                </LabelText>
              )}
            </div>

            {/* Preview text */}
            <MutedText className="line-clamp-2 text-xs leading-relaxed sm:text-sm">
              {item.preview}
            </MutedText>

            {/* Info badges (bookmark date and/or card info) */}
            {(bookmarkInfo || cardInfo) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {bookmarkInfo && (
                  <BookmarkInfoBadge bookmarkInfo={bookmarkInfo} />
                )}
                {cardInfo && <CardInfoBadge cardInfo={cardInfo} />}
              </div>
            )}
          </div>
        </button>
      )}
      {onRemove && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100"
          onClick={handleRemove}
          disabled={isRemoving}
          aria-label="Remove from collection"
        >
          <IconX className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
